import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon, PlainButton, inkA, orange, tokens } from 'components/v3';
import { closeHelpPanel, openHelpPanel, useHelpPanel } from './helpPanelStore';
import { activeHost, useTourState } from './tourStore';

// "Help" in a Forms page header: opens the Help & tours panel below it.
// `compact` shows the icon alone; `size` is the square's side in px (44 on
// phones, next to the other header buttons); `muted` matches the phone
// header's tinted icon buttons. It only shows on pages that mount FormsHelp
// (the panel renders from there), so it never sits on a page as a dead
// button.

const Button = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: ${({ $size }) => $size}px;
  padding: 0 12px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid ${({ $t }) => $t.border};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color 150ms ease, background 150ms ease;

  &:hover,
  &[aria-expanded='true'] {
    border-color: ${orange(0.55)};
    background: ${orange(0.08)};
  }

  ${({ $compact, $size }) =>
    $compact &&
    css`
      width: ${$size}px;
      padding: 0;
    `}
`;

const HelpButton = ({ compact, label, size, muted }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { open } = useHelpPanel();
  const ref = useRef(null);
  useTourState();
  if (!activeHost()) return null;
  const onClick = () => {
    if (open) {
      closeHelpPanel();
      return;
    }
    openHelpPanel(ref.current ? ref.current.getBoundingClientRect() : null);
  };
  return (
    <Button
      ref={ref}
      $t={t}
      $compact={compact}
      $size={size}
      $bg={muted ? inkA(theme, 0.04) : 'transparent'}
      $color={muted ? t.sub : t.text}
      data-tour="help-button"
      aria-label="Help & tours"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onClick}
    >
      <Icon name="circle-help" size={compact && size >= 44 ? 19 : 17} />
      {compact ? null : <span>{label}</span>}
    </Button>
  );
};

HelpButton.propTypes = {
  compact: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.number,
  muted: PropTypes.bool,
};

HelpButton.defaultProps = {
  compact: false,
  label: 'Help',
  size: 36,
  muted: false,
};

export default HelpButton;
